import { useQuery } from "@tanstack/react-query";
import { Loader2, ReceiptText, ClipboardList } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const inr = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 }));
const dmy = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

export function ExecDrilldown({ row, onClose }) {
  const key = row?.executive_key;
  const { data, isLoading } = useQuery({
    queryKey: ["exec-drill", key],
    queryFn: async () => (await api.get(`/billing/executive-drilldown/${encodeURIComponent(key)}`)).data,
    enabled: !!key,
  });

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="exec-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-baseline gap-3">
            <span>{row?.executive}</span>
            <span className="font-mono text-xs font-normal text-slate-500">
              target {row?.target_cr?.toFixed(2)} · invoiced {row?.achieved_cr?.toFixed(2)} · booked {row?.booked_cr?.toFixed(2)} · run-rate {row?.run_rate_pct?.toFixed(1)}%
            </span>
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <Tabs defaultValue="invoices">
            <TabsList>
              <TabsTrigger value="invoices" data-testid="exec-drill-tab-invoices">
                <ReceiptText className="h-3.5 w-3.5 mr-1.5" /> Invoices ({data?.invoice_count || 0} · {data?.invoiced_cr || 0})
              </TabsTrigger>
              <TabsTrigger value="orders" data-testid="exec-drill-tab-orders">
                <ClipboardList className="h-3.5 w-3.5 mr-1.5" /> Booked Orders ({data?.order_count || 0} · {data?.orders_value_cr || 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="invoices" className="mt-3">
              {!data?.invoices?.length ? (
                <p className="py-8 text-center text-sm text-slate-400" data-testid="exec-drill-invoices-empty">No invoices yet this FY.</p>
              ) : (
                <div className="border rounded-lg overflow-x-auto" data-testid="exec-drill-invoice-table">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        {["Invoice", "Date", "Customer", "Project", "City", "Type", "Value"].map((h) => (
                          <th key={h} className="px-3 py-2 whitespace-nowrap">{h}</th>))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.invoices.map((g) => (
                        <tr key={g.invoice_no}>
                          <td className="px-3 py-1.5 font-mono whitespace-nowrap">{g.invoice_no}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-slate-500">{dmy(g.invoice_date)}</td>
                          <td className="px-3 py-1.5 max-w-[200px] truncate">{g.customer_name}</td>
                          <td className="px-3 py-1.5 max-w-[180px] truncate text-slate-500">{g.project_name || "—"}</td>
                          <td className="px-3 py-1.5 text-slate-500">{g.city || "—"}</td>
                          <td className="px-3 py-1.5 text-slate-500">{g.order_type || "—"}</td>
                          <td className="px-3 py-1.5 font-mono text-right whitespace-nowrap">{inr(g.taxable_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="orders" className="mt-3">
              {!data?.orders?.length ? (
                <p className="py-8 text-center text-sm text-slate-400" data-testid="exec-drill-orders-empty">
                  No booked orders captured yet — orders appear from the daily Milestone PDFs.
                </p>
              ) : (
                <div className="border rounded-lg overflow-x-auto" data-testid="exec-drill-order-table">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        {["S.O. No.", "S.O. Date", "Client", "Project / Country", "Industry", "Items", "Value"].map((h) => (
                          <th key={h} className="px-3 py-2 whitespace-nowrap">{h}</th>))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.orders.map((o, i) => (
                        <tr key={i}>
                          <td className="px-3 py-1.5 font-mono whitespace-nowrap">{o.so_no || "—"}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-slate-500">{o.so_date || dmy(o.report_date)}</td>
                          <td className="px-3 py-1.5 max-w-[200px] truncate">{o.client_name}</td>
                          <td className="px-3 py-1.5 max-w-[180px] truncate text-slate-500">{o.project_name || "—"}</td>
                          <td className="px-3 py-1.5 text-slate-500">{o.industry_type || "—"}</td>
                          <td className="px-3 py-1.5 text-slate-500">{o.items}</td>
                          <td className="px-3 py-1.5 font-mono text-right whitespace-nowrap">{inr(o.so_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="mt-2 text-[10px] text-slate-400">Booked orders are the day-wise sales orders extracted from received Milestone PDFs (history builds up as daily reports arrive).</p>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
